import axios from 'axios';

import { SERVER_URL } from '../constants/url';
import CODE from '../constants/code';
import URL from '../constants/url';
import MSG from '../constants/message';
import {
    getDpAuthRefeshTokenSimple,
    getLoadingHandler,
} from '../assets/js/comm';

/*****************
* 기본 API 인스턴스
*****************/
const instance = axios.create({
    baseURL: SERVER_URL,
    headers: {
        'Content-Type': 'application/json',
    },
    withCredentials: true,
});

// saveRefreshToken 전용 인스턴스.
// instance에 등록한 인증/로딩 인터셉터를 사용하지 않는다.
const tokenSaveInstance = axios.create({
    baseURL: SERVER_URL,
    headers: {
        'Content-Type': 'application/json',
    },
    withCredentials: true,
    timeout: 15000,
});

/*****************
* 인증 상태
*****************/
// 모든 대기 요청이 같은 Promise를 기다린다.
let refreshPromise = null;

// 갱신 완료 후 늦게 도착한 이전 요청의 401을 구분한다.
let authVersion = 0;

// 세션 종료 후 추가 갱신/중복 알림을 방지한다.
let sessionTerminated = false;

const AUTH_HOSTS = new Set([
    'auth-cmccareplus.cmcnu.or.kr',
    'dev-auth-cmccareplus.cmcnu.or.kr',
]);

const SAVE_REFRESH_TOKEN_PATH = '/signup/saveRefreshToken';

/*****************
* 전역 로딩
*****************/
const LOADING_DELAY = 100;

let pendingCount = 0;
let delayTimer = null;
let loadingShown = false;

const showLoading = () => {
    const setLoading = getLoadingHandler();

    if (setLoading) {
        setLoading(true);
        loadingShown = true;
    }
};

const clearLoadingTimer = () => {
    if (delayTimer !== null) {
        clearTimeout(delayTimer);
        delayTimer = null;
    }
};

const hideLoading = () => {
    const setLoading = getLoadingHandler();

    if (setLoading) {
        setLoading(false);
    }

    loadingShown = false;
};

const startLoading = (config) => {
    if (config.noLoading || config._loadingTracked) {
        return;
    }

    config._loadingTracked = true;
    pendingCount += 1;

    if (pendingCount === 1 && delayTimer === null && !loadingShown) {
        delayTimer = setTimeout(() => {
            delayTimer = null;

            if (pendingCount > 0 && !loadingShown) {
                showLoading();
            }
        }, LOADING_DELAY);
    }
};

const finishLoading = (config) => {
    // 이 요청이 실제로 증가시킨 카운트만 감소시킨다.
    if (!config?._loadingTracked) {
        return;
    }

    config._loadingTracked = false;
    pendingCount = Math.max(0, pendingCount - 1);

    if (pendingCount === 0) {
        clearLoadingTimer();

        if (loadingShown) {
            hideLoading();
        }
    }
};

const resetLoading = () => {
    pendingCount = 0;
    clearLoadingTimer();
    hideLoading();
};

/*****************
* 공통 유틸리티
*****************/
const isResultCode = (value, expected) => {
    if (
        value === undefined ||
        value === null ||
        expected === undefined ||
        expected === null
    ) {
        return false;
    }

    return Number(value) === Number(expected);
};

const getRequestUrl = (config) => {
    try {
        // 절대 URL 요청과 baseURL + 상대 URL 요청을 모두 처리한다.
        return new window.URL(
            instance.getUri(config),
            window.location.href
        );
    } catch {
        return null;
    }
};

const isMainApiRequest = (config) => {
    const requestUrl = getRequestUrl(config);

    if (!requestUrl) {
        return false;
    }

    try {
        const serverUrl = new window.URL(
            SERVER_URL,
            window.location.href
        );

        return (
            requestUrl.origin === serverUrl.origin &&
            !AUTH_HOSTS.has(requestUrl.hostname)
        );
    } catch {
        return false;
    }
};

const isRefreshExcluded = (config) => {
    if (config.skipAuthRefresh) {
        return true;
    }

    const requestUrl = getRequestUrl(config);

    if (!requestUrl) {
        return true;
    }

    const pathname = requestUrl.pathname.replace(/\/+$/, '');

    return (
        AUTH_HOSTS.has(requestUrl.hostname) ||
        pathname.endsWith(SAVE_REFRESH_TOKEN_PATH)
    );
};

const shouldManageAuth = (config) => {
    return (
        isMainApiRequest(config) &&
        !isRefreshExcluded(config) &&
        !config.skipAuthToken
    );
};

const setAuthorization = (config, accessToken) => {
    config.headers = config.headers || {};

    // Axios 1.x의 AxiosHeaders와 일반 객체를 모두 처리한다.
    if (typeof config.headers.set === 'function') {
        config.headers.set('Authorization', `Bearer ${accessToken}`);
        return;
    }

    Object.keys(config.headers).forEach((key) => {
        if (key.toLowerCase() === 'authorization') {
            delete config.headers[key];
        }
    });

    config.headers.Authorization = `Bearer ${accessToken}`;
};

const makeSessionError = (message = '인증 세션이 종료되었습니다.') => {
    const error = new Error(message);
    error.code = 'AUTH_SESSION_TERMINATED';
    return error;
};

/*****************
 세션 정리 및 이동
*****************/
const clearingSessionUserMove = () => {
    localStorage.removeItem('jToken');
    localStorage.removeItem('AFTCR_CRTFC_TKN');

    // 기존 프로젝트 동작 유지.
    sessionStorage.clear();

    window.dispatchEvent(new Event('auth-changed'));
    window.location.replace(URL.MAIN);
};

const terminateSession = (message) => {
    if (sessionTerminated) {
        return;
    }

    sessionTerminated = true;
    resetLoading();

    if (message) {
        alert(message);
    }

    clearingSessionUserMove();
};

/*****************
* 실제 토큰 갱신 작업
******************/
const performTokenRefresh = async () => {
    if (sessionTerminated) {
        throw makeSessionError();
    }

    const loginUser = JSON.parse(
        sessionStorage.getItem('loginUser') || 'null'
    );

    if (!loginUser?.usrUid || !loginUser?.crtfcTknVal) {
        throw makeSessionError(MSG.ERR_SESSION);
    }

    // 1. 인증 서버에서 새 토큰 발급.
    const tokenData = await getDpAuthRefeshTokenSimple(
        loginUser.crtfcTknVal
    );

    const accessToken = tokenData?.accessToken;
    const refreshToken = tokenData?.refreshToken;

    if (
        typeof accessToken !== 'string' ||
        !accessToken.trim() ||
        typeof refreshToken !== 'string' ||
        !refreshToken.trim()
    ) {
        throw new Error('토큰 갱신 응답에 유효한 토큰이 없습니다.');
    }

    // 갱신 도중 다른 요청이 세션을 종료했으면 저장하지 않는다.
    if (sessionTerminated) {
        throw makeSessionError();
    }

    // 2. 새 refresh token을 업무 서버에 저장.
    const saveResp = await tokenSaveInstance.post(
        SAVE_REFRESH_TOKEN_PATH,
        {
            crtfcTknVal: refreshToken,
            usrInfoId: loginUser.usrInfoId,
            usrUid: loginUser.usrUid,
        },
        {
            headers: {
                Authorization: `Bearer ${accessToken}`,
            },
        }
    );

    if (!isResultCode(saveResp?.data?.resultCode, CODE.RCV_SUCCESS)) {
        throw new Error('리프레쉬 토큰 업데이트에 실패하였습니다.');
    }

    if (sessionTerminated) {
        throw makeSessionError();
    }

    // 3. 첫 번째 프로젝트의 응답 구조 사용.
    const returnedUserInfo = saveResp?.data?.result?.userInfo;

    if (
        !returnedUserInfo ||
        typeof returnedUserInfo !== 'object' ||
        Array.isArray(returnedUserInfo)
    ) {
        throw new Error('토큰 저장 응답에 userInfo가 없습니다.');
    }

    const updatedLoginUser = {
        ...loginUser,
        ...returnedUserInfo,

        // 저장 API 응답에 오래된 토큰이 포함되더라도
        // 실제 발급된 refresh token으로 확정한다.
        crtfcTknVal: refreshToken,
    };

    if (
        String(updatedLoginUser.usrUid) !== String(loginUser.usrUid)
    ) {
        throw new Error('토큰 저장 응답의 사용자 정보가 일치하지 않습니다.');
    }

    // 서버 저장 성공 후 클라이언트 저장소 반영.
    localStorage.setItem('jToken', accessToken);
    localStorage.setItem('AFTCR_CRTFC_TKN', refreshToken);
    sessionStorage.setItem(
        'loginUser',
        JSON.stringify(updatedLoginUser)
    );

    authVersion += 1;

    return accessToken;
};

/*****************
* refresh 단일 실행
*****************/
const getSharedRefreshPromise = () => {
    if (!refreshPromise) {
        refreshPromise = performTokenRefresh()
            .catch((error) => {
                const message =
                    error.code === 'AUTH_SESSION_TERMINATED'
                        ? MSG.ERR_SESSION
                        : '인증정보 업데이트에 실패하였습니다.';

                // 공유 Promise 내부에서 한 번만 로그아웃 처리.
                terminateSession(message);

                throw error;
            })
            .finally(() => {
                refreshPromise = null;
            });
    }

    return refreshPromise;
};

/*****************
* Request 인터셉터
*****************/
instance.interceptors.request.use(
    async (config) => {
        try {
            if (config.baseURL === SERVER_URL && !config.origin) {
                config.origin = SERVER_URL;
            }

            if (shouldManageAuth(config)) {
                if (sessionTerminated) {
                    throw makeSessionError();
                }

                // 갱신 중 새로 발생한 일반 API 요청도 기다린다.
                // 인증 서버 / 토큰 저장 요청은 이 대기 대상에서 제외된다.
                if (refreshPromise) {
                    await refreshPromise;
                }

                if (sessionTerminated) {
                    throw makeSessionError();
                }

                // 최신 access token을 매 요청에 반영.
                const accessToken = localStorage.getItem('jToken');

                if (accessToken) {
                    setAuthorization(config, accessToken);
                }

                // 실제 전송 시점의 인증 버전 기록.
                config._authVersion = authVersion;
            }

            // 실제 전송 직전에 로딩 카운트 증가.
            startLoading(config);

            return config;
        } catch (error) {
            // 요청 인터셉터에서 발생한 오류에도 config를 연결한다.
            if (!error.config) {
                error.config = config;
            }

            finishLoading(config);

            throw error;
        }
    },
    (error) => {
        finishLoading(error.config);
        return Promise.reject(error);
    }
);

/*****************
* Response 인터셉터
*****************/
instance.interceptors.response.use(
    (resp) => {
        finishLoading(resp.config);

        // 기존 첫 번째 프로젝트의 업무 인증 오류 처리 유지.
        if (
            isResultCode(
                resp.data?.resultCode,
                CODE.RCV_ERROR_AUTH
            )
        ) {
            const error = makeSessionError(MSG.ERR_SESSION);

            error.config = resp.config;
            error.response = resp;

            terminateSession(MSG.ERR_SESSION);

            // return false 대신 명시적인 실패로 전달.
            return Promise.reject(error);
        }

        return resp;
    },
    async (error) => {
        const originalRequest = error.config;

        finishLoading(originalRequest);

        if (axios.isCancel(error)) {
            return Promise.reject(error);
        }

        if (error.code === 'ERR_NETWORK') {
            if (!sessionTerminated) {
                alert(MSG.ERR_NETWORK);
            }

            return Promise.reject(error);
        }

        const status = error.response?.status;
        const resultCode = error.response?.data?.resultCode;
        const businessCode = error.response?.data?.result?.code;

        if (businessCode === 'DUPLICATE_LOGIN') {
            terminateSession(
                '다른 기기에서 로그인되어 로그아웃되었습니다.'
            );

            return Promise.reject(error);
        }

        if (businessCode === 'SESSION_EXPIRED') {
            terminateSession(MSG.ERR_SESSION);
            return Promise.reject(error);
        }

        if (!originalRequest || !error.response) {
            return Promise.reject(error);
        }

        if (sessionTerminated) {
            return Promise.reject(error);
        }

        const isUnauthorized =
            status === 401 ||
            isResultCode(resultCode, CODE.RCV_BAD_UNAUTH);

        if (!isUnauthorized) {
            return Promise.reject(error);
        }

        // 인증 서버 및 refresh token 저장 요청은 자동 갱신 제외.
        // 외부 서버 요청도 업무 서버 토큰으로 재시도하지 않는다.
        if (
            !isMainApiRequest(originalRequest) ||
            isRefreshExcluded(originalRequest) ||
            originalRequest.skipAuthToken
        ) {
            return Promise.reject(error);
        }

        // 동일 요청은 최대 한 번만 재시도.
        if (originalRequest._retry) {
            return Promise.reject(error);
        }

        originalRequest._retry = true;

        let accessToken;

        try {
            const latestAccessToken = localStorage.getItem('jToken');

            // 이전 토큰으로 보낸 요청의 401이 갱신 완료 후 늦게 도착:
            // 추가 refresh 없이 최신 access token으로 재시도한다.
            if (
                originalRequest._authVersion !== undefined &&
                originalRequest._authVersion !== authVersion &&
                latestAccessToken
            ) {
                // 그 사이 또 다른 갱신이 시작되었다면 그 결과를 기다린다.
                accessToken = refreshPromise
                    ? await refreshPromise
                    : latestAccessToken;
            } else {
                // 최초 요청만 갱신을 시작하고 나머지는 같은 Promise 대기.
                accessToken = await getSharedRefreshPromise();
            }
        } catch (refreshError) {
            return Promise.reject(refreshError);
        }

        if (sessionTerminated) {
            return Promise.reject(makeSessionError());
        }

        // 대기 중 취소된 요청은 재전송하지 않는다.
        if (originalRequest.signal?.aborted) {
            return Promise.reject(
                new axios.CanceledError('토큰 갱신 대기 중 요청이 취소되었습니다.')
            );
        }

        setAuthorization(originalRequest, accessToken);

        // 기본 axios가 아닌 기존 커스텀 인스턴스로 재시도.
        // 재시도 자체의 실패는 refresh 실패와 구분한다.
        return instance(originalRequest);
    }
);

export default instance;
